import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { BookOpen, ListChecks, Lightbulb, ShieldAlert, HelpCircle } from "lucide-react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-border bg-card p-6 sm:p-8">
      <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-foreground">
        <Icon className="h-5 w-5 text-brand" /> {title}
      </h2>
      {children}
    </section>
  );
}

export default function EducationalContent({ calc }) {
  const { content, faq } = calc;
  if (!content) return null;

  return (
    <div className="space-y-6">
      <Section icon={BookOpen} title="How it works" testid="content-how-it-works">
        <p className="text-base leading-relaxed text-foreground/80">{content.howItWorks}</p>
      </Section>

      {content.inputsExplained?.length > 0 && (
        <Section icon={ListChecks} title="Inputs explained" testid="content-inputs">
          <dl className="grid gap-4 sm:grid-cols-2">
            {content.inputsExplained.map((it) => (
              <div key={it.term} className="rounded-lg bg-secondary/50 p-4">
                <dt className="font-heading text-sm font-bold text-brand">{it.term}</dt>
                <dd className="mt-1 text-sm text-foreground/80">{it.desc}</dd>
              </div>
            ))}
          </dl>
        </Section>
      )}

      {content.example && (
        <Section icon={Lightbulb} title="Worked example" testid="content-example">
          <p className="text-base leading-relaxed text-foreground/80">{content.example}</p>
        </Section>
      )}

      {(content.assumptions?.length || content.limitations?.length) > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {content.assumptions?.length > 0 && (
            <Section icon={ListChecks} title="Assumptions" testid="content-assumptions">
              <ul className="space-y-2">
                {content.assumptions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" /> {a}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {content.limitations?.length > 0 && (
            <Section icon={ShieldAlert} title="Limitations" testid="content-limitations">
              <ul className="space-y-2">
                {content.limitations.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-action" /> {a}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}

      {faq?.length > 0 && (
        <Section icon={HelpCircle} title="Frequently asked questions" testid="content-faq">
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-foreground/80">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>
      )}
    </div>
  );
}
